import React, { useState, useEffect } from 'react';
import { Briefcase, GraduationCap, Award, Wrench, Edit3, Settings, Moon, Sun, Share2, Copy, Check, Mail, Phone, MapPin, Github, Globe, Linkedin, Twitter, Eye, EyeOff, Lock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getIconForType } from '../../utils/socialLinks/socialLinkIcons';

// 공유 모달 컴포넌트
const ShareModal = ({ isOpen, onClose, portfolioUrl, portfolioOwnerName }) => {
  const [copied, setCopied] = useState(false);
  // const [activeTab, setActiveTab] = useState('link'); // 사용하지 않으므로 주석 처리

  // 공유 URL과 텍스트 설정
  const shareData = {
    url: portfolioUrl,
    text: `${portfolioOwnerName}님의 웹 페이지를 확인해보세요!`,
    hashtags: ['웹 페이지', 'resume', 'latte']
  };

  // URL 복사 기능
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareData.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // 클립보드 API가 지원되지 않는 경우 fallback
      const textArea = document.createElement('textarea');
      textArea.value = shareData.url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // 모바일에서 네이티브 공유 API 사용
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${portfolioOwnerName}님의 웹 페이지를 확인해보세요`,
          url: shareData.url,
        });
      } catch (err) {
        console.log('공유가 취소되었거나 오류가 발생했습니다.');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">내 웹페이지 공유하기</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* 링크 복사 컨텐츠 */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <input
                type="text"
                value={shareData.url}
                readOnly
                className="flex-1 bg-transparent text-sm text-gray-700 outline-none"
              />
              <button
                onClick={copyToClipboard}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? '복사됨!' : '복사'}
              </button>
            </div>
            
            {/* 네이티브 공유 (모바일) */}
            {navigator.share && (
              <button
                onClick={handleNativeShare}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                <Share2 className="w-5 h-5" />
                기기에서 공유하기
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PortfolioTemplateDefault = ({ portfolio, isOwner, currentLanguage, hasEnglishVersion, onLanguageChange, onEditClick, visibility, onVisibilityChange }) => {
  const [resumeData, setResumeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  
  // 다크 모드 토글 함수
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // 실시간 이력서 데이터 조회
  useEffect(() => {
    const fetchResumeData = async () => {
      if (!portfolio?.session_id) {
        setLoading(false);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('resumes')
          .select('*')
          .eq('session_id', portfolio.session_id)
          .eq('is_active', true)
          .single();
          
        if (error) {
          console.error('이력서 데이터 조회 오류:', error);
          // resume_snapshot을 사용하지 않고 오류 처리
          setResumeData(null);
        } else {
          const formattedData = {
            name: data.name,
            name_en: data.name_en, // 영어 이름 추가
            title: data.title,
            summary: data.summary,
            experiences: data.experiences || [],
            education: data.education || [],
            skills: data.skills || [],
            certificates: data.certificates || [],
            keyStrengths: data.key_strengths || [],
            papers: data.papers || [],
            phone: data.phone,
            email: data.email,
            github: data.github,
            address: data.address,
            social_links: data.social_links || [],
            english_version: data.english_version // 영어 버전 데이터 추가
          };
          setResumeData(formattedData);
        }
      } catch (err) {
        console.error('예상치 못한 오류:', err);
        // resume_snapshot을 사용하지 않고 오류 처리
        setResumeData(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchResumeData();
  }, [portfolio?.session_id]); // portfolio.resume_snapshot 의존성 제거
  
  // 현재 선택된 언어에 따라 표시할 데이터 결정
  const getDisplayData = () => {
    if (!resumeData) return null;
    
    if (currentLanguage === 'en' && resumeData.english_version) {
      return {
        ...resumeData,
        name: resumeData.name_en || resumeData.english_version.name || resumeData.name, // name_en 우선 사용
        title: resumeData.english_version.title || resumeData.title,
        summary: resumeData.english_version.summary || resumeData.summary,
        keyStrengths: resumeData.english_version.key_strengths || resumeData.keyStrengths,
        experiences: resumeData.english_version.experiences || resumeData.experiences,
        education: resumeData.english_version.education || resumeData.education,
        skills: resumeData.english_version.skills || resumeData.skills,
        certificates: resumeData.english_version.certificates || resumeData.certificates,
        papers: resumeData.english_version.papers || resumeData.papers
      };
    }
    
    return resumeData;
  };

  const displayData = getDisplayData();
  
  if (!portfolio) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-500">웹 페이지 데이터를 불러오는 중입니다...</p>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-500">이력서 데이터를 불러오는 중입니다...</p>
        </div>
      </div>
    );
  }

  const { profile_image_url, original_image_url, ai_enhancement_data } = portfolio;
  
  const getDisplayImageUrl = () => {
    if (ai_enhancement_data && ai_enhancement_data.selected_type) {
      const selectedType = ai_enhancement_data.selected_type;
      
      switch (selectedType) {
        case 'original':
          return original_image_url || profile_image_url;
        case 'gfpgan':
          return ai_enhancement_data.gfpgan_url || profile_image_url;
        case 'gfpgan-bg-remover':
          return profile_image_url;
        case 'gfpgan-bg-remover-bw':
          return ai_enhancement_data.black_white_url || profile_image_url;
        case 'final':
          return ai_enhancement_data.final_url || profile_image_url;
        default:
          return profile_image_url;
      }
    }
    
    return original_image_url || profile_image_url;
  };
  
  const displayImageUrl = getDisplayImageUrl();
  
  if (!displayData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">이력서 데이터를 불러올 수 없습니다.</p>
          <p className="text-sm text-gray-400">이력서가 삭제되었거나 비활성화된 상태일 수 있습니다.</p>
        </div>
      </div>
    );
  }

  const {
    name,
    title,
    summary,
    experiences,
    skills,
    education,
    certificates,
    keyStrengths,
    papers,
    phone,
    email,
    github,
    address,
    social_links
  } = displayData;

  // 테마 클래스 정의
  const themeClasses = darkMode 
    ? {
        background: 'bg-gray-900',
        cardBg: 'bg-gray-800 border-gray-700',
        textPrimary: 'text-white',
        textSecondary: 'text-gray-300',
        textMuted: 'text-gray-400',
        accent: 'text-blue-400',
        pattern: 'opacity-5'
      }
    : {
        background: 'bg-white',
        cardBg: 'bg-white border-gray-200',
        textPrimary: 'text-gray-900',
        textSecondary: 'text-gray-700',
        textMuted: 'text-gray-500',
        accent: 'text-blue-600',
        pattern: 'opacity-20'
      };

  // 섹션 컴포넌트
  const Section = ({ title, children, sectionKey }) => {
    const hasContent = Array.isArray(children) ? children.some(child => child) : children;
    if (!hasContent) return null;

    // visibility 설정에 따라 숨김 처리
    const isVisible = visibility?.[sectionKey] !== false;
    if (!isVisible && !isOwner) return null;

    // 공개 상태일 때만 눈 아이콘 표시
    const showVisibilityToggle = isOwner && onVisibilityChange && portfolio?.status === 'published';

    return (
      <section className={`mb-12 ${!isVisible && isOwner ? 'opacity-50' : ''}`}>
        <div className="mb-8 flex items-center justify-between">
          <h2 className={`text-lg font-semibold ${themeClasses.textPrimary} uppercase tracking-wider`}>{title}</h2>
          {showVisibilityToggle && (
            <div className="relative group">
              <button
                onClick={() => onVisibilityChange(sectionKey, !isVisible)}
                className={`p-2 rounded-lg transition-colors ${
                  darkMode
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-100'
                }`}
                title={isVisible ? '클릭하여 이 섹션 숨기기' : '클릭하여 이 섹션 보이기'}
              >
                {isVisible ? (
                  <Eye className={`w-4 h-4 ${themeClasses.textMuted}`} />
                ) : (
                  <EyeOff className={`w-4 h-4 ${themeClasses.textMuted}`} />
                )}
              </button>
              {/* 툴팁 */}
              <div className="absolute right-0 top-full mt-2 w-64 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                {isVisible 
                  ? '클릭하면 다른 사용자에게 이 섹션이 보이지 않습니다' 
                  : '클릭하면 다른 사용자에게 이 섹션이 다시 보입니다'
                }
                <div className="absolute top-0 right-4 -mt-1 w-2 h-2 bg-gray-800 transform rotate-45" />
              </div>
            </div>
          )}
        </div>
        <div className="space-y-8">
          {children}
        </div>
      </section>
    );
  };

  // 언어에 따른 섹션 타이틀 정의
  const getSectionTitles = () => {
    if (currentLanguage === 'en') {
      return {
        intro: 'Intoroduction',
        keyStrengths: 'Core Competencies',
        experience: 'Professional Experience',
        education: 'Education',
        papers: 'Research & Publications',
        certificates: 'Certifications & Awards',
        skills: 'Skills'
      };
    }
    
    return {
      intro: '소개',
      keyStrengths: '핵심 역량',
      experience: '경력',
      education: '학력',
      papers: '논문 및 연구',
      certificates: '자격증 및 수상 내역',
      skills: '보유 기술'
    };
  };

  const sectionTitles = getSectionTitles();

  // 블릿포인트 처리 헬퍼 함수
  const renderBulletPoints = (text, isProject = false) => {
    if (!text) return null;
    
    // 줄 단위로 분리
    const lines = text.split('\n');
    const processedLines = [];
    let hasBullets = false;
    
    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return; // 빈 줄은 건너뛰기
      
      // 블릿포인트로 시작하는 줄 체크 (-, •, *, ·)
      const bulletMatch = trimmedLine.match(/^[-•*·]\s*(.*)/);
      
      if (bulletMatch) {
        // 블릿포인트가 있는 경우
        hasBullets = true;
        processedLines.push(
          <li key={index} className="flex items-start">
            <span className={`mr-2 mt-0.5 ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              •
            </span>
            <span className="flex-1 leading-relaxed">{bulletMatch[1]}</span>
          </li>
        );
      } else if (isProject && trimmedLine) {
        // 프로젝트 설명에서 블릿포인트가 없는 텍스트도 블릿포인트로 만들기
        hasBullets = true;
        processedLines.push(
          <li key={index} className="flex items-start">
            <span className={`mr-2 mt-0.5 ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              •
            </span>
            <span className="flex-1 leading-relaxed">{trimmedLine}</span>
          </li>
        );
      } else if (trimmedLine) {
        // 일반 텍스트인 경우 (경력 설명)
        processedLines.push(
          <div key={index} className="mb-1">
            {trimmedLine}
          </div>
        );
      }
    });
    
    if (hasBullets || isProject) {
      return <ul className="space-y-1.5 list-none">{processedLines}</ul>;
    } else {
      return <div className="whitespace-pre-line leading-relaxed">{text}</div>;
    }
  };

  // 경력 항목 컴포넌트
  const ExperienceItem = ({ experience }) => (
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="lg:w-1/4">
        <div className={`text-sm font-medium ${themeClasses.textMuted} mb-1`}>
          {experience.startDate} - {experience.endDate}
        </div>
        <p className={`font-medium ${themeClasses.textSecondary}`}>{experience.company}</p>
      </div>
      <div className="lg:w-3/4">
        <h3 className={`text-lg font-semibold ${themeClasses.textPrimary} mb-3`}>{experience.position}</h3>
        {experience.overallDescription && (
          <div className={`${themeClasses.textSecondary} mb-5`}>
            {renderBulletPoints(experience.overallDescription, false)}
          </div>
        )}
        {experience.projects && experience.projects.length > 0 && (
          <div className="space-y-4">
            {experience.projects.map((project, index) => (
              <div key={index} className={`relative ${
                darkMode ? 'bg-gray-800/30' : 'bg-gray-50'
              } rounded-lg p-4 border-l-4 ${
                darkMode ? 'border-gray-600' : 'border-gray-300'
              }`}>
                <h4 className={`font-semibold ${themeClasses.textPrimary} mb-3`}>
                  {project.projectName}
                </h4>
                <div className={`text-sm ${themeClasses.textSecondary}`}>
                  {renderBulletPoints(project.projectDescription, true)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // 교육 항목 컴포넌트
  const EducationItem = ({ edu }) => (
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="lg:w-1/4">
        <div className={`text-sm font-medium ${themeClasses.textMuted}`}>
          {edu.startDate} - {edu.endDate}
        </div>
      </div>
      <div className="lg:w-3/4">
        <h3 className={`text-lg font-semibold ${themeClasses.textPrimary}`}>{edu.school}</h3>
        <p className={`text-md ${themeClasses.textSecondary} mt-1`}>{edu.degree} - {edu.major}</p>
      </div>
    </div>
  );

  return (
    <div className={`font-sans antialiased ${themeClasses.background} min-h-screen relative`}>
      {/* 비공개 상태 안내 배너 - 소유자에게만 표시 (최상단 고정) */}
      {isOwner && portfolio?.status !== 'published' && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-50 border-b border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center space-x-2">
              <Lock className="w-5 h-5 text-yellow-600" />
              <p className="text-sm text-yellow-800">
                해당 포트폴리오는 비공개 상태입니다. 공개를 원하시면 수정 화면에서 공개하기를 해주세요.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className={`relative z-10 flex ${isOwner && portfolio?.status !== 'published' ? 'pt-12' : ''}`}>
        {/* Left Column - Scrollable Content */}
        <main className={`${displayImageUrl ? 'w-full lg:w-3/5 xl:w-7/12' : 'w-full'} overflow-y-auto`}>
          <div className={`p-8 sm:p-12 md:p-16 ${!displayImageUrl ? 'max-w-5xl mx-auto' : ''}`}>

            <header className="mb-12">
              <div className="pb-8 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <h1 className={`text-4xl lg:text-5xl font-bold ${themeClasses.textPrimary} ${
                        visibility?.showName === false && !isOwner ? 'hidden' : ''
                      } ${
                        visibility?.showName === false && isOwner ? 'opacity-50' : ''
                      }`}>
                        {name || '이름 없음'}
                      </h1>
                      {isOwner && (
                        <div className="flex gap-2">
                          <button
                            onClick={onEditClick}
                            className={`p-2 rounded-lg ${
                              darkMode 
                                ? 'text-gray-400 hover:text-blue-400' 
                                : 'text-gray-400 hover:text-blue-600'
                            }`}
                            title="웹 페이지 설정 (주소/이미지 수정)"
                          >
                            <Settings className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => window.open(`/resume/${portfolio.session_id}`, '_blank', 'noopener,noreferrer')}
                            className={`p-2 rounded-lg ${
                              darkMode 
                                ? 'text-gray-400 hover:text-green-400' 
                                : 'text-gray-400 hover:text-green-600'
                            }`}
                            title="이력서 내용 수정"
                          >
                            <Edit3 className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className={`text-lg ${themeClasses.textSecondary} font-medium mb-3 ${
                      visibility?.showTitle === false && !isOwner ? 'hidden' : ''
                    } ${
                      visibility?.showTitle === false && isOwner ? 'opacity-50' : ''
                    }`}>
                      {title || '직무/타이틀 없음'}
                    </div>
                    {/* 연락처 정보 */}
                    <div className={`flex flex-wrap gap-8 text-sm ${themeClasses.textMuted} ${
                      visibility?.showContact === false && !isOwner ? 'hidden' : ''
                    } ${
                      visibility?.showContact === false && isOwner ? 'opacity-50' : ''
                    }`}>
                      {email && (
                        <a 
                          href={`mailto:${email}`} 
                          className={`hover:${themeClasses.accent} transition-colors flex items-center gap-1`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {email}
                        </a>
                      )}
                      {phone && (
                        <a 
                          href={`tel:${phone}`} 
                          className={`hover:${themeClasses.accent} transition-colors flex items-center gap-1`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {phone}
                        </a>
                      )}
                      
                      {/* social_links 배열에서 링크 표시 */}
                      {social_links && social_links.map((link, index) => {
                        if (!link.url) return null;
                        
                        const platform = link.type || link.platform || '';
                        const url = link.url || '';
                        let displayText = url;
                        
                        // URL에서 프로토콜과 www 제거
                        displayText = displayText.replace(/^https?:\/\/(www\.)?/, '');
                        
                        // 특별한 경우 처리
                        if (platform === 'location') {
                          return (
                            <span key={index} className={`flex items-center gap-1`}>
                              <MapPin className="w-4 h-4" />
                              {url}
                            </span>
                          );
                        }
                        
                        // 링크인 경우
                        return (
                          <a 
                            key={index}
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`hover:${themeClasses.accent} transition-colors flex items-center gap-1`}
                          >
                            {getIconForType(link.icon || link.type)}
                            {displayText}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                  <nav className="flex items-center gap-6 text-sm font-medium">
                    {/* 언어 토글 버튼 - 영어 버전이 있을 때만 표시 */}
                    {hasEnglishVersion && (
                      <div className="flex items-center gap-2">
                        {/* 한국어 라디오 버튼 */}
                        <button
                          onClick={() => onLanguageChange('kr')}
                          className={`flex items-center gap-2 px-3 py-2 text-sm rounded transition-all ${
                            currentLanguage === 'kr'
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                          title="한국어 버전 보기"
                        >
                          <div className={`w-3 h-3 rounded-full border flex items-center justify-center ${
                            currentLanguage === 'kr' 
                              ? 'border-gray-900 bg-gray-900' 
                              : 'border-gray-400'
                          }`}>
                            {currentLanguage === 'kr' && (
                              <div className="w-1 h-1 bg-white rounded-full"></div>
                            )}
                          </div>
                          <span>KR</span>
                        </button>
                        
                        {/* 영어 라디오 버튼 */}
                        <button
                          onClick={() => onLanguageChange('en')}
                          className={`flex items-center gap-2 px-3 py-2 text-sm rounded transition-all ${
                            currentLanguage === 'en'
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                          title="English Version 보기"
                        >
                          <div className={`w-3 h-3 rounded-full border flex items-center justify-center ${
                            currentLanguage === 'en' 
                              ? 'border-gray-900 bg-gray-900' 
                              : 'border-gray-400'
                          }`}>
                            {currentLanguage === 'en' && (
                              <div className="w-1 h-1 bg-white rounded-full"></div>
                            )}
                          </div>
                          <span>EN</span>
                        </button>
                      </div>
                    )}
                    
                    <a href="#" className={`${themeClasses.textMuted} hover:${themeClasses.accent}`}>Home</a>
                    <span 
                      className={`${themeClasses.textMuted} hover:${themeClasses.accent} cursor-pointer relative group`}
                      title="7월 오픈 예정입니다"
                    >
                      Blog
                      {/* 커스텀 툴팁 */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                        7월 오픈 예정입니다
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-800"></div>
                      </div>
                    </span>
                    {/* 공유 아이콘 - 공개 상태일 때만 표시 */}
                    {portfolio?.status === 'published' && (
                      <button
                        onClick={() => setShowShareModal(true)}
                        className={`flex items-center gap-1 ${themeClasses.textMuted} hover:${themeClasses.accent} transition-colors`}
                        title="웹 페이지 공유하기"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                    )}
                  </nav>
                </div>
              </div>
            </header>

            <div>
              {/* 요약 섹션 */}
              <Section title={sectionTitles.intro} sectionKey="showSummary">
                <p className={`text-lg ${themeClasses.textSecondary} leading-relaxed`}>
                  {summary || (currentLanguage === 'en' ? 'No summary available.' : '요약 정보가 없습니다.')}
                </p>
              </Section>
              
              {/* 핵심 역량 */}
              <Section title={sectionTitles.keyStrengths} sectionKey="showKeyStrengths">
                {keyStrengths && keyStrengths.length > 0 ? (
                  <div className="space-y-4">
                    {keyStrengths.map((strength, index) => (
                      <div key={index} className={`pl-4 border-l-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                        <p className={`${themeClasses.textSecondary} leading-relaxed`}>{strength}</p>
                      </div>
                    ))}
                  </div>
                ) : <p className={themeClasses.textMuted}>{currentLanguage === 'en' ? 'No core competencies available.' : '핵심 역량 정보가 없습니다.'}</p>}
              </Section>
              
              {/* 경력 */}
              <Section title={sectionTitles.experience} sectionKey="showExperience">
                {experiences && experiences.length > 0 ? (
                  <div className="space-y-8">
                    {experiences.map((exp, index) => <ExperienceItem key={index} experience={exp} />)}
                  </div>
                ) : <p className={themeClasses.textMuted}>{currentLanguage === 'en' ? 'No experience information available.' : '경력 정보가 없습니다.'}</p>}
              </Section>

              {/* 학력 */}
              <Section title={sectionTitles.education} sectionKey="showEducation">
                {education && education.length > 0 ? (
                  <div className="space-y-6">
                    {education.map((edu, index) => <EducationItem key={index} edu={edu} />)}
                  </div>
                ): <p className={themeClasses.textMuted}>{currentLanguage === 'en' ? 'No education information available.' : '학력 정보가 없습니다.'}</p>}
              </Section>
              
              {/* 논문 및 연구 - 데이터가 있을 때만 표시 */}
              {papers && papers.length > 0 && (
                <Section title={sectionTitles.papers} sectionKey="showPapers">
                  <div className="space-y-6">
                    {papers.map((paper, index) => (
                      <div key={index} className={`pl-4 border-l-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                        <h4 className={`font-semibold ${themeClasses.textPrimary} text-lg mb-2`}>{paper.title}</h4>
                        <p className={`text-sm ${themeClasses.textMuted} mb-2`}>
                          {paper.authors} - {paper.journal || paper.conference} ({paper.year})
                        </p>
                        {paper.description && (
                          <p className={`${themeClasses.textSecondary} text-sm leading-relaxed`}>{paper.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>
              )}
              
              {/* 자격증 */}
              <Section title={sectionTitles.certificates} sectionKey="showCertificates">
                {certificates && certificates.length > 0 ? (
                  <div className="space-y-3">
                    {certificates.map((cert, index) => (
                      <div key={index}>
                        <span className={`font-medium ${themeClasses.textPrimary}`}>{cert.name}</span>
                        <span className={`${themeClasses.textMuted} text-sm ml-2`}>
                          — {cert.issuer}{cert.date ? ` (${cert.date})` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                ): <p className={themeClasses.textMuted}>{currentLanguage === 'en' ? 'No certifications available.' : '자격증 정보가 없습니다.'}</p>}
              </Section>
              
              {/* 보유 기술 */}
              <Section title={sectionTitles.skills} sectionKey="showSkills">
                {skills && skills.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {skills.map((skill, index) => (
                      <span key={index} className={`px-3 py-1 text-sm rounded-md border ${
                        darkMode 
                          ? 'bg-gray-800 border-gray-600 text-gray-300' 
                          : 'bg-gray-50 border-gray-300 text-gray-700'
                      }`}>
                        {skill}
                      </span>
                    ))}
                  </div>
                ) : <p className={themeClasses.textMuted}>{currentLanguage === 'en' ? 'No skills information available.' : '기술 정보가 없습니다.'}</p>}
              </Section>
            </div>
          </div>
        </main>

        {/* Right Column - Fixed Image - 이미지가 있을 때만 표시 */}
        {displayImageUrl && (visibility?.showProfileImage !== false || isOwner) && (
          <aside className={`hidden lg:block lg:w-2/5 xl:w-5/12 fixed right-0 top-0 h-screen ${
            visibility?.showProfileImage === false && isOwner ? 'opacity-50' : ''
          }`}>
            <div className="w-full h-full relative overflow-hidden">
              {/* 이미지 표시/숨김 토글 버튼 - 소유자이고 공개 상태일 때만 */}
              {isOwner && onVisibilityChange && portfolio?.status === 'published' && (
                <div className="absolute top-4 right-4 z-10 group">
                  <button
                    onClick={() => onVisibilityChange('showProfileImage', !(visibility?.showProfileImage !== false))}
                    className={`p-2 rounded-lg bg-black bg-opacity-50 hover:bg-opacity-70 transition-all`}
                    title={visibility?.showProfileImage !== false ? '클릭하여 프로필 이미지 숨기기' : '클릭하여 프로필 이미지 보이기'}
                  >
                    {visibility?.showProfileImage !== false ? (
                      <Eye className="w-4 h-4 text-white" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-white" />
                    )}
                  </button>
                  {/* 툴팁 */}
                  <div className="absolute right-0 top-full mt-2 w-64 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    {visibility?.showProfileImage !== false 
                      ? '클릭하면 다른 사용자에게 프로필 이미지가 보이지 않습니다' 
                      : '클릭하면 다른 사용자에게 프로필 이미지가 다시 보입니다'
                    }
                    <div className="absolute top-0 right-4 -mt-1 w-2 h-2 bg-gray-800 transform rotate-45" />
                  </div>
                </div>
              )}
              <img 
                src={displayImageUrl} 
                alt={`${name || '사용자'}의 프로필 사진`}
                className="w-full h-full object-cover object-center"
                onError={(e) => { 
                  // 이미지 로드 실패 시 아예 숨김
                  e.target.style.display = 'none';
                  e.target.parentElement.parentElement.style.display = 'none';
                }}
              />
            </div>
          </aside>
        )}
      </div>
      
      {/* 공유 모달 */}
      <ShareModal 
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        portfolioUrl={`${window.location.origin}/${portfolio.slug}`}
        portfolioOwnerName={name || '사용자'}
      />
    </div>
  );
};

export default PortfolioTemplateDefault;